import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../toaster';
import { EventState, Event, EventFilters, EventStats, EventBookedUser, EventRegistration, EventRegistrationUploadResult, CorporateEventUser } from '../../types/events';

const BASE = '/events';

export async function uploadEventManual(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('files', file);
    const response = await doPOST('/file/upload/event-manuals', formData);
    if (!response || response.status < 200 || response.status >= 400) {
        throw new Error(response?.message || 'Failed to upload user manual');
    }
    const url = response.data?.data?.files?.[0]?.url;
    if (typeof url !== 'string' || !/^\/api\/static\/event-manuals\/[a-zA-Z0-9._-]+\.pdf$/.test(url)) {
        throw new Error('User manual upload did not return a valid PDF URL');
    }
    return url;
}

export async function fetchCorporateEventUsers(corporateId: string): Promise<CorporateEventUser[]> {
    const query = new URLSearchParams({ corporateId, userType: 'user' });
    const response = await doGET(`/users?${query.toString()}`);
    if (response.status < 200 || response.status >= 400) {
        throw new Error(response.message || 'Failed to fetch corporate users');
    }
    const users = response.data?.data?.users;
    return Array.isArray(users) ? users : [];
}



const store = create<EventState>((set, get) => ({
    data: [],
    totalPages: 0,
    currentPage: 1,
    filters: {},
    isLoading: false,
    rows: 20,
    total: 0,
    allData: [],

    fetchGrid: async () => {
        try {
            const { filters, currentPage, rows, isLoading, total } = get();
            if (isLoading) return;

            set({ isLoading: true });

            const queryParams = new URLSearchParams(filters as any);
            queryParams.append('page', String(currentPage));
            queryParams.append('limit', String(rows));
            const apiUrl = `${BASE}?${queryParams.toString()}`;

            const response = await doGET(apiUrl);

            if (response.status >= 200 && response.status < 400) {
                const events: Event[] = Array.isArray(response.data?.data) ? response.data.data : [];
                const eventsWithStats = await Promise.all(events.map(async (event: Event) => {
                    if (!event._id) return event;
                    const stats = await get().fetchStats(event._id);
                    return { ...event, stats: stats || undefined };
                }));
                set({
                    data: eventsWithStats,
                    totalPages: response.data?.pagination?.totalPages ?? 1,
                    total: response.data?.pagination?.total ?? eventsWithStats.length,
                });
            } else {
                showError(response.message);
            }
        } catch (err) {
            showError('Failed to fetch Events');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchGridAll: async (filters: EventFilters) => {
        try {
            const { allData } = get();
            if (allData.length > 0) return allData;
            const queryParams = new URLSearchParams(filters as any);
            const apiUrl = `${BASE}?${queryParams.toString()}`;

            const response = await doGET(apiUrl);

            if (response.status >= 200 && response.status < 400) {
                set({ allData: Array.isArray(response.data?.data) ? response.data.data : [], });
            }
            return get().allData;
        } catch (err) {
            showError('Failed to fetch Events');
            return get().allData
        }
    },

    setFilters: (newFilters: EventFilters) => {
        set({ filters: newFilters, currentPage: 1 });
        get().fetchGrid(1, newFilters);
    },

    nextPage: () => {
        const { currentPage, totalPages, fetchGrid } = get();
        if (currentPage < totalPages) {
            set(state => ({
                currentPage: state.currentPage + 1
            }))
            fetchGrid();
        }
    },

    prevPage: () => {
        const { currentPage, filters, fetchGrid } = get();
        if (currentPage > 1) {
            set(state => ({
                currentPage: state.currentPage - 1
            }))
            fetchGrid();
        }
    },
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        const { currentPage, filters, fetchGrid, totalPages } = get();
        if (currentPage > 1 || currentPage <= totalPages) {
            set({
                currentPage: page + 1
            })
            fetchGrid();
        }
    },

    onCreate: async (data: any) => {
        try {
            const response = await doPOST(BASE, data);
            console.log(response);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            }
            return response?.data?.data
        } catch (error) {
            return null;
        }
    },
    onUpdate: async (data: Event) => {
        try {
            if (!data._id) {
                showError('Event ID is required for update');
                return null;
            }
            const response = await doPUT(`${BASE}/${data._id}`, data);
            console.log(response);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            }
            return response?.data?.data
        } catch (error) {
            return null;
        }
    },
    onDelete: async (id: string) => {
        try {
            const response = await doDELETE(`${BASE}/${id}`);
            console.log(response);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            }
        } catch (error) {

        }
    },
    detail: async (id: string) => {
        try {
            const response = await doGET(`${BASE}/${id}`);
            return response.data;
        } catch (error) {

        }
    },
    fetchStats: async (id: string): Promise<EventStats | null> => {
        try {
            const response = await doGET(`${BASE}/${id}/stats`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data || null;
            }
            return null;
        } catch (error) {
            return null;
        }
    },
    fetchBookedUsers: async (id: string): Promise<EventBookedUser[]> => {
        try {
            const response = await doGET(`${BASE}/${id}/bookings`);
            if (response.status >= 200 && response.status < 400) {
                return Array.isArray(response.data?.data) ? response.data.data : [];
            }
            showError(response.message || 'Failed to fetch event bookings');
            return [];
        } catch (error) {
            showError('Failed to fetch event bookings');
            return [];
        }
    },
    fetchRegistrations: async (id: string): Promise<EventRegistration[]> => {
        try {
            const response = await doGET(`${BASE}/${id}/registrations`);
            if (response.status >= 200 && response.status < 400) {
                return Array.isArray(response.data?.data) ? response.data.data : [];
            }
            showError(response.message || 'Failed to fetch event registrations');
            return [];
        } catch (error) {
            showError('Failed to fetch event registrations');
            return [];
        }
    },
    registerUsers: async (id: string, userIds: string[]): Promise<EventRegistrationUploadResult | null> => {
        try {
            const response = await doPOST(`${BASE}/${id}/registrations`, { userIds });
            if (response.status >= 200 && response.status < 400) {
                const result = response.data?.data as EventRegistrationUploadResult | undefined;
                if (result?.registered.length) showSuccess(`${result.registered.length} user(s) registered for the event`);
                get().fetchGrid();
                return result || null;
            }
            showError(response.message || 'Failed to register users');
            return null;
        } catch (error) {
            showError('Failed to register users');
            return null;
        }
    },
    uploadRegistrations: async (id: string, file: File): Promise<EventRegistrationUploadResult | null> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await doPOST(`${BASE}/${id}/registrations/upload`, formData);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                return response.data?.data || null;
            }
            showError(response.message || 'Failed to upload event registrations');
            return null;
        } catch (error) {
            showError('Failed to upload event registrations');
            return null;
        }
    },

}));



export const useEventStore = () => store((state) => state);
